import { agoText, dateText, dateTimeText } from '../../../../../../shared/date/local-time'
import { connectionLabel, isManaged, isScim } from '../common/managed-by'
import { providerName, removalConnection } from '../removal-words'

export { providerName, removalConnection }

// What the person page says about one member of the organization (owner
// report 2026-10-29: "this page looks very crappy and broken"): who they
// are, whether they can get in, how they sign in, how they were added, and
// what removing, restoring or deleting them does. One place, so the header,
// the cards and the confirmations read alike.

export const MICROSOFT_ENTRA_ID = 'Microsoft Entra ID'

// "Microsoft Entra ID (EdXR Azure Dev)" or "your identity provider".
export const providerOrYours = conn => providerName(conn) || 'your identity provider'

// The member's status: { key, label, tone } for the header's pill.
//   removed   Removed (bad)
//   invited   Invited, Invite expired, Invite revoked, Invite declined (warn)
//   active    Active (ok)
export const statusOf = ({ removed, person }) => {
  if (removed) return { key: 'removed', label: 'Removed', tone: 'bad' }
  const status = person && person.inviteStatus
  if (status === 'pending') return { key: 'invited', label: 'Invited', tone: 'warn' }
  if (status === 'expired') return { key: 'invited', label: 'Invite expired', tone: 'warn' }
  if (status === 'revoked') return { key: 'invited', label: 'Invite revoked', tone: 'warn' }
  if (status === 'declined') return { key: 'invited', label: 'Invite declined', tone: 'warn' }
  return { key: 'active', label: 'Active', tone: 'ok' }
}

// How they sign in to this organization, in a sentence.
export const signInText = managedBy => {
  if (!isManaged(managedBy)) return 'Their own EducationXR sign-in, not your single sign-on'
  return `Single sign-on with ${providerOrYours(managedBy)}`
}

// The header's line about who manages them: "Provisioned by EdXR Azure Dev"
// or "Signs in with School Okta" (the Users list's words), '' otherwise.
export const managedLine = managedBy => {
  if (!isManaged(managedBy)) return ''
  const label = connectionLabel(managedBy) || 'your identity provider'
  return isScim(managedBy) ? `Provisioned by ${label}` : `Signs in with ${label}`
}

// When and how they were added: { when, how } (how may be '').
export const addedText = person => {
  if (!person) return { when: '', how: '' }
  const when = person.createdAt ? dateText(person.createdAt) : ''
  const status = person.inviteStatus
  let how = ''
  if (status === 'accepted') how = person.inviteRegisteredAt ? `Invited, accepted ${dateText(person.inviteRegisteredAt)}` : 'Invited, accepted'
  else if (status === 'member') how = 'Invited; joined through single sign-on before accepting'
  else if (status) how = person.inviteInvitedAt ? `Invited ${dateText(person.inviteInvitedAt)}` : 'Invited'
  else if (isScim(person.managedBy)) how = `Provisioned by ${providerOrYours(person.managedBy)}`
  return { when, how }
}

// When they were last active: "3 hours ago" with the exact time, or "Never".
export const lastActiveText = value => (value ? { text: agoText(value), title: dateTimeText(value) } : { text: 'Never', title: '' })

// The removed member's notice: what it means and what brings them back
// (`means` and `back`: sentences, the ways back in order).
export const removedNotice = ({ row, canRestore }) => {
  const via = row && row.removedVia
  const conn = removalConnection(row)
  const here = canRestore ? 'You restore them here, with Restore at the top of this page.' : 'An admin who manages users restores them here.'
  const back = []
  if (via === 'scim') {
    back.push(`${conn ? providerName(conn) : 'Your identity provider'} assigns them to EducationXR again. Their membership comes back as it was.`)
    back.push(here)
  } else if (via === 'self') {
    back.push('They join again.')
    back.push(here)
  } else {
    back.push(here)
    back.push('An admin invites them again.')
  }
  return {
    means: [
      "They can't sign in to this organization or open its courses.",
      'Their learning records and the content they made are kept.'
    ],
    back
  }
}

// The read-only note under the groups, courses and roles of a removed
// member.
export const KEPT_NOTE = 'Their roles, groups and courses are kept as they were for when they are restored, and can’t be changed while they are removed.'

// Restore's confirmation. A SCIM removal says, truthfully, that the identity
// provider still counts them as removed (content's RestoreMemberTx clears
// only our record; the provider's next deactivation removes them again).
export const restoreDialog = ({ name, row }) => {
  const title = `Restore ${name || 'this person'}?`
  const body = ['They become a member of this organization again, with their groups, courses and learning records. Roles you can’t give yourself aren’t given back.']
  if (row && row.removedVia === 'scim') {
    const who = providerName(removalConnection(row)) || 'Your identity provider'
    body.push(`${who} removed them and still counts them as removed: restoring them here doesn’t change that. If it sends their removal again, they’re removed again. To bring them back for good, assign them again in your identity provider.`)
  }
  return { title, body, confirm: 'Restore' }
}

// "Remove from organization" (DELETE /user-login/:id: content's member
// removal, a soft delete; the person page's old "Delete").
export const removeDialog = ({ name, managedBy }) => {
  const body = [
    `${name || 'This person'} loses access to this organization at once. Their account isn’t deleted.`,
    'Their groups, courses, roles and learning records are kept, and you can restore them from this page or the Users recycle bin. Single sign-on won’t add them back until you do.'
  ]
  if (isScim(managedBy)) body.push(`${providerOrYours(managedBy)} still has them assigned, so it can add them back. To remove them for good, unassign them there.`)
  return { title: `Remove ${name || 'this person'} from this organization?`, body, confirm: 'Remove from organization' }
}

// "Delete permanently" for a removed member (DELETE /user-login/:id/purge:
// the recycle bin's purge, a hard delete of the membership row; its groups,
// courses, roles and learning records in this organization go with it).
export const purgeDialog = ({ name }) => ({
  title: `Permanently delete ${name || 'this person'} from this organization?`,
  body: [
    'Their membership here is deleted for good, with their groups, courses and roles and their learning records in this organization (sessions, assessment results and saved progress). This can’t be undone.',
    'Their EducationXR account isn’t deleted, and their other organizations aren’t touched.'
  ],
  confirm: 'Delete permanently'
})
